import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import LandingNavbar from '@/components/LandingNavbar';
import { LandingHeroVideo } from '@/components/LandingHeroVideo';
import ForensicScorecard from '@/components/scorecard/ForensicScorecard';
import HeroKpiBand from '@/components/marketing/HeroKpiBand';
import LiveSignalPanel from '@/components/marketing/LiveSignalPanel';
import EvidenceLayer from '@/components/marketing/EvidenceLayer';
import DesignedFor from '@/components/marketing/DesignedFor';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import {
  LANDING_SAMPLE_PROJECT,
  LANDING_SAMPLE_SCORECARD,
} from '@/config/landingSampleScorecard';

const LANDING_PREVIEW_SESSION_ID = 'landing-preview-session';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="relative min-h-screen bg-black ni-grid-bg">
      <LandingHeroVideo />
      <LandingNavbar variant="dark" overlay />

      <main className="relative z-10">
        {/* Hero */}
        <section className="pt-24 pb-12">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 xl:gap-12 items-start">
              <div className="space-y-6 max-w-xl">
                <p className="ni-label text-white/40">Forensic screenplay intelligence</p>
                <h1 className="text-3xl sm:text-4xl md:text-5xl font-sans font-bold text-white leading-[1.12] tracking-tight">
                  Development mandates.
                  <br />
                  Nuclius Index clearance.
                  <br />
                  Board-ready audit.
                </h1>
                <div className="flex flex-wrap gap-4">
                  <Button variant="indexPrimary" onClick={() => navigate('/login')}>
                    Access workspace
                  </Button>
                  <Button
                    variant="indexOutline"
                    onClick={() => navigate('/enterprise')}
                    className="border-white/25 text-white hover:bg-white/5"
                  >
                    Enterprise access
                  </Button>
                </div>
                <p className="text-white/45 text-sm font-mono leading-relaxed max-w-md">
                  NUCLIUS INDEX scores screenplays against mandate fit, attention resonance, and
                  commercial signal — calibrated for slate decisions.
                </p>
              </div>
              <div className="w-full max-w-md sm:max-w-lg xl:max-w-xl xl:ml-auto">
                <p className="ni-label mb-3 text-white/35">Example forensic scorecard</p>
                <div className="ni-panel p-5">
                  <ForensicScorecard
                    scorecard={LANDING_SAMPLE_SCORECARD}
                    scriptTitle={LANDING_SAMPLE_PROJECT}
                    sessionId={LANDING_PREVIEW_SESSION_ID}
                  />
                </div>
              </div>
            </div>
            <div className="mt-10">
              <HeroKpiBand />
              <p className="text-[9px] font-mono text-white/20 mt-2 uppercase">Illustrative platform metrics</p>
            </div>
          </div>
        </section>

        {/* Live signal + CTA band */}
        <section className="py-12 border-t border-[#2a2a2a]">
          <div className="container mx-auto px-4 max-w-6xl grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <LiveSignalPanel />
            <div className="ni-panel p-6">
              <p className="ni-label mb-2">Get started</p>
              <h2 className="text-xl font-sans font-bold text-white mb-3">
                Ingest your first screenplay today
              </h2>
              <p className="text-sm text-white/45 font-sans mb-6">
                Self-serve workspace access or institutional procurement with custom SLAs and SAML/OIDC.
              </p>
              <div className="flex flex-wrap gap-3">
                <Button variant="indexPrimary" onClick={() => navigate('/register')}>
                  Create account
                </Button>
                <Button variant="indexOutline" onClick={() => navigate('/enterprise')}>
                  Talk to sales
                </Button>
              </div>
            </div>
          </div>
        </section>

        <EvidenceLayer />
        <DesignedFor />

        <section className="py-16 border-t border-[#2a2a2a]">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <p className="ni-label mb-3">Ready</p>
            <h2 className="text-2xl font-sans font-bold text-white mb-6">
              Clear your slate with forensic evidence
            </h2>
            <Button variant="indexPrimary" onClick={() => navigate('/login')}>
              Access Nuclius Index
            </Button>
          </div>
        </section>
      </main>

      <MarketingFooter />
    </div>
  );
};

export default LandingPage;
